import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  GIFT_ALERT_LABELS,
  GIFT_PURCHASE_STATUS_LABELS,
  formatIls,
  giftAdmin,
  type GiftAlertRow,
  type GiftPurchaseIntentRow,
  type GiftWebhookEventRow,
} from '@/lib/giftCards';

const FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'הכול' },
  { value: 'pending', label: 'ממתין לתשלום' },
  { value: 'paid', label: 'שולם' },
  { value: 'issued', label: 'הונפק' },
  { value: 'failed', label: 'נכשל' },
  { value: 'needs_reconciliation', label: 'דרושה בדיקה' },
];

/** Site gift-card orders: intent → payment → issuance → delivery. */
export default function GiftPurchaseAdmin() {
  const [status, setStatus] = useState('');
  const [intents, setIntents] = useState<GiftPurchaseIntentRow[]>([]);
  const [alerts, setAlerts] = useState<GiftAlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{
    intent: GiftPurchaseIntentRow | null;
    events: GiftWebhookEventRow[];
    alerts: GiftAlertRow[];
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, alertList] = await Promise.all([
        giftAdmin<{ intents: GiftPurchaseIntentRow[] }>('purchases.list', { status }),
        giftAdmin<{ alerts: GiftAlertRow[] }>('alerts.list', { only_open: true }),
      ]);
      setIntents(list.intents ?? []);
      setAlerts(alertList.alerts ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'טעינת הרכישות נכשלה');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (id: string) => {
    if (openId === id) {
      setOpenId(null);
      setDetail(null);
      return;
    }
    setOpenId(id);
    setDetail(null);
    try {
      const res = await giftAdmin<{
        intent: GiftPurchaseIntentRow;
        webhook_events: GiftWebhookEventRow[];
        alerts: GiftAlertRow[];
      }>('purchase.detail', { intent_id: id });
      setDetail({ intent: res.intent, events: res.webhook_events ?? [], alerts: res.alerts ?? [] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'טעינת הפרטים נכשלה');
    }
  };

  const retry = async (id: string) => {
    setBusy(true);
    try {
      await giftAdmin('purchase.retry', { intent_id: id });
      toast.success('ההנפקה הושלמה');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'הניסיון נכשל');
    } finally {
      setBusy(false);
    }
  };

  const resolveAlert = async (id: string) => {
    setBusy(true);
    try {
      await giftAdmin('alert.resolve', { alert_id: id, status: 'resolved' });
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'סגירת ההתראה נכשלה');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {alerts.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-red-900">
            <ShieldAlert className="h-4 w-4" /> התראות שדורשות בדיקה ידנית ({alerts.length})
          </div>
          {alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 text-sm text-red-900">
              <span>
                {GIFT_ALERT_LABELS[a.kind] ?? a.kind}
                {(a.occurrences ?? 1) > 1 && (
                  <span className="text-xs ms-2 rounded-full bg-red-100 px-2 py-0.5">×{a.occurrences}</span>
                )}
                <span className="opacity-60 text-xs ms-2">
                  {new Date(a.last_seen_at ?? a.created_at).toLocaleString('he-IL')}
                </span>
              </span>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => resolveAlert(a.id)}>
                סימון כטופל
              </Button>
            </div>
          ))}
          <p className="text-xs text-red-900/70">
            החזרים וביטולים אינם מבצעים חיוב או ביטול אוטומטי של שובר — ההחלטה נשארת בידיים שלך.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatus(f.value)}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${
              status === f.value ? 'bg-neutral-900 text-white border-neutral-900' : 'hover:bg-neutral-100'
            }`}
          >
            {f.label}
          </button>
        ))}
        <Button size="sm" variant="outline" className="ms-auto gap-2" onClick={() => void load()}>
          <RefreshCw className="h-4 w-4" /> רענון
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : intents.length === 0 ? (
        <div className="rounded-xl border bg-white p-8 text-center text-sm text-muted-foreground">
          אין רכישות שוברים מהאתר בטווח שנבחר.
        </div>
      ) : (
        <div className="rounded-xl border bg-white divide-y">
          {intents.map((it) => (
            <div key={it.id}>
              <button onClick={() => void openDetail(it.id)} className="w-full text-start px-4 py-3 hover:bg-neutral-50 transition">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium">{it.recipient_name}</span>
                  <span className="text-sm text-muted-foreground">{formatIls(Number(it.amount))}</span>
                  <StatusPill status={it.status} />
                  {it.order_name && <span className="text-xs text-muted-foreground">{it.order_name}</span>}
                  <span className="text-xs text-muted-foreground ms-auto">
                    {new Date(it.created_at).toLocaleString('he-IL')}
                  </span>
                </div>
              </button>

              {openId === it.id && (
                <div className="px-4 pb-4 space-y-3 bg-neutral-50/60">
                  {!detail ? (
                    <div className="py-4 text-muted-foreground text-sm flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> טוען…
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 text-sm">
                        <Field label="סכום" value={formatIls(Number(it.amount))} />
                        <Field label="שולם" value={it.paid_amount ? formatIls(Number(it.paid_amount)) : '—'} />
                        <Field label="הזמנה" value={it.order_name ?? '—'} />
                        <Field
                          label="הנפקה"
                          value={it.issued_at ? new Date(it.issued_at).toLocaleString('he-IL') : '—'}
                        />
                        <Field label="מקבל/ת" value={it.recipient_name} />
                        <Field label="מייל" value={it.recipient_email ?? '—'} />
                        <Field label="כשל" value={it.failure_code ?? '—'} />
                        <Field label="בדיקה" value={it.reconciliation_reason ?? '—'} />
                      </div>

                      <div className="text-xs text-muted-foreground">
                        קוד השובר המלא לעולם אינו מוצג כאן — הוא נגלה רק למקבל/ת לאחר אימות חד-פעמי.
                      </div>

                      <div>
                        <div className="text-xs font-medium mb-1">אירועי שופיפיי</div>
                        <div className="space-y-1">
                          {detail.events.length === 0 ? (
                            <div className="text-xs text-muted-foreground">אין אירועים</div>
                          ) : (
                            detail.events.map((e) => (
                              <div key={e.id} className="text-xs flex items-center gap-2">
                                <span className="font-mono">{e.topic}</span>
                                <span>{e.status}</span>
                                <span className="opacity-60">{e.outcome ?? ''}</span>
                                <span className="opacity-50 ms-auto">
                                  {new Date(e.created_at).toLocaleString('he-IL')}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {it.status === 'rejected' && it.failure_code === 'issue_failed' ? (
                        <Button size="sm" disabled={busy} onClick={() => void retry(it.id)} className="gap-2">
                          {busy && <Loader2 className="h-4 w-4 animate-spin" />} ניסיון הנפקה חוזר
                        </Button>
                      ) : (
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                          ניסיון חוזר מתאפשר רק לכשל סופי לפני הנפקה. במצב לא ודאי נדרשת בדיקה ידנית בשופיפיי.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'issued'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : status === 'needs_reconciliation'
        ? 'bg-red-50 text-red-800 border-red-200'
        : status === 'rejected'
          ? 'bg-amber-50 text-amber-900 border-amber-200'
          : 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${tone}`}>
      {status === 'issued' && <CheckCircle2 className="h-3 w-3 inline-block me-1 -mt-0.5" />}
      {GIFT_PURCHASE_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
